import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, OnInit, Output, Input, ViewChild, inject, Renderer2, signal, computed } from '@angular/core';
import { CommonModule, NgSwitch } from '@angular/common';
import { AbstractControl, ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Question } from 'src/app/models/question.model';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DragStore } from 'src/app/store/drag-store';
import { EQuestionType } from 'src/app/models/questionType.model';
import { EStatus } from 'src/app/models/EnumStatus';
import { MenuQuestionComponent } from '../question-form/components/menu-question/menu-question.component';
import { MiniOperationsStore } from 'src/app/store/mini-operations-store';
import { QuestionFormComponent } from '../question-form/question-form.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DisplayByTypeDirective } from './display-by-type.directive';
import { SmartTooltipComponent } from './components/smart-tooltip/smart-tooltip.component';
import { TitleQuestionComponent } from './components/title-question/title-question.component';
import { MatDialog } from '@angular/material/dialog';
import { SaveNameListComponent } from '../question-form/components/save-name-list/save-name-list.component';
import { OptionsListApiService } from 'src/app/features/collections/services/options-list-api.service';
import { OptionsList } from 'src/app/models/optionsList.model';
import { BigTitleQuestionComponent } from './components/big-title-question/big-title-question.component';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { GlobalStore } from 'src/app/store/global-store';
import { DisplayApiFieldNameComponent } from './components/display-api-field-name/display-api-field-name.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environment';
import { DisplayFilterFieldComponent } from "./components/display-api-field-name/display-filter-field/display-filter-field.component";
import { DisplayAutoFillFieldComponent } from "./components/display-api-field-name/display-auto-fill-field/display-auto-fill-field.component";
import { EQuestionAction } from 'src/app/models/EnumQuestionAction';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
    selector: 'que-question',
    imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        QuestionFormComponent,
        TitleQuestionComponent,
        MatInputModule,
        MatFormFieldModule,
        MenuQuestionComponent,
        TranslateDirective, TranslatePipe,
        DisplayByTypeDirective,
        SmartTooltipComponent,
        BigTitleQuestionComponent,
        ToggleComponent,
        DisplayApiFieldNameComponent, DisplayFilterFieldComponent, DisplayAutoFillFieldComponent, SvgIconComponent],
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: QuestionComponent,
            multi: true
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: QuestionComponent,
            multi: true
        }
    ],
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionComponent implements OnInit, ControlValueAccessor, Validator {
  @ViewChild('formQuestion') mydiv: ElementRef | undefined;
  @Input() index: number = -4;
  @Output() myDropEvevt: EventEmitter<CdkDragDrop<string[]>> = new EventEmitter();
  @Output() deleteMeEvent = new EventEmitter();
  @Output() dupliacteQuestionEvent = new EventEmitter();

  eQuestionType = EQuestionType;
  #dragStore = inject(DragStore);
  #listServiceApi = inject(OptionsListApiService);
  #globalStore = inject(GlobalStore);
  #dialog = inject(MatDialog)

  questionForm = new FormControl<Question | null>(null, [Validators.required]);

  titleAndText = new FormControl<{ title?: string, text?: string, titleToData?: string } | null>(null, [Validators.required]);
  focusId$ = this.#dragStore.focusId$;
  myType = 0;
  registerOnChangeCallback: any = () => { };
  onValidationChangeCallBack: any;
  questionStatus?: EStatus;
  isDragEvent$ = this.#dragStore.isDrag$;
  isShowFill = this.questionForm.value?.type?.id != this.eQuestionType.Divider;
  
  // Compute field object dynamically from ID for translate pipe
  #exposureFields = toSignal(this.#globalStore.exposureFields$, { initialValue: [] });
  #apiExposureFieldId = signal<number | undefined>(undefined);
  
  apiExposureFieldSig$ = computed(() => {
    const id = this.#apiExposureFieldId();
    if (!id) return null;

    const fields = this.#exposureFields();
    return fields.find(f => f.id === id) || null;
  });

  #tableFields = toSignal(this.#globalStore.tableFields$, { initialValue: [] });
  #autoFillFieldId = signal<number | undefined>(undefined);

  autoFillFieldSig$ = computed(() => {
    const id = this.#autoFillFieldId();
    if (!id) return null;

    const fields = this.#tableFields();
    return fields.find(f => f.id === id) || null;
  });

  ngOnInit(): void {
    this.titleAndText.valueChanges.subscribe(
      (titleAndText) => {
        var newText = this.questionForm.value?.type.id == (this.eQuestionType.Link) ? this.questionForm.value?.text : titleAndText?.text;
        if (this.questionStatus == EStatus.Saved)
          this.questionStatus = EStatus.Changed;
        this.registerOnChangeCallback({
          ...this.questionForm.value, status: this.questionStatus,
          text: newText, title: titleAndText?.title, titleToData: titleAndText?.titleToData
        } as Question);
      });

    this.questionForm.valueChanges.subscribe((question) => {
      var newText = question?.type.id == this.eQuestionType.Text || this.eQuestionType.Link ? question?.text : this.titleAndText.value?.text;
      if (this.questionStatus == EStatus.Saved)
        this.questionStatus = EStatus.Changed;
      this.registerOnChangeCallback({
        ...question, status: this.questionStatus,
        text: newText, title: this.titleAndText.value?.title, titleToData: this.titleAndText.value?.titleToData
      } as Question);
    });
  }
  ngAfterViewInit() {
    if (this.mydiv) {
      // this.scrollIntoViewCenter(this.mydiv.nativeElement);
    }
  }

  private scrollIntoViewCenter(element: HTMLElement) {
    const container = document.querySelector('.board-edit'); // adjust the selector accordingly

    if (container) {
      const containerHeight = container.clientHeight;
      const elementHeight = element.clientHeight;

      const scrollToMiddle = (elementHeight - containerHeight) / 2;
      container.scrollTop = element.offsetTop - scrollToMiddle;
    }
  }
  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    if (this.questionForm.valid) {
      return null;
    }
    return { 'stepForm': "not-valid" }
  }
  writeValue(obj: Question): void {
    this.questionStatus = obj.status;
    this.myType = obj.type.id;
    this.#apiExposureFieldId.set(obj.apiExposureFieldId);  // Set ID, name computed automatically
    this.#autoFillFieldId.set(obj.autoFillFieldId);
    this.questionForm.patchValue({ ...obj }, { emitEvent: false });
    this.titleAndText.patchValue({ text: obj?.text, title: obj?.title, titleToData: obj?.titleToData })
    // //this.onValidationChangeCallBack();xxx
  }
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChangeCallBack = fn;
  }

  registerOnChange(fn: any): void {
    this.registerOnChangeCallback = fn;
  }
  registerOnTouched(fn: any): void {
  }
  eventMenu(event: EQuestionAction) {

    switch (event) {
      case EQuestionAction.delete:
        this.deleteMeEvent.emit();
        break;

      case EQuestionAction.addToCollection:
        this.#dragStore.addToCollection(this.index)
        break;
      case EQuestionAction.saveList :
        this.#dragStore.saveList(this.index);
        break;
      case EQuestionAction.chooseAsFilterField:
        this.#dragStore.setUnauthorizedFilterField({ index: this.index, isEditMode: false });
        break;
      case EQuestionAction.removeAsFilterField:
        this.#dragStore.setUnauthorizedFilterField(undefined);
        this.questionForm.patchValue({ ...this.questionForm.value, isFilterField: false } as Question);
        break;
      case EQuestionAction.chooseAsAutoFillField:
        this.#dragStore.setAutoFillField({ index: this.index, isEditMode: false });
        break;
      case EQuestionAction.editAsAutoFillField:
        this.#dragStore.setAutoFillField({ index: this.index, isEditMode: true });
        break;
      case EQuestionAction.removeAsAutoFillField:
        this.questionForm.patchValue({ ...this.questionForm.value, autoFillFieldId: undefined } as Question);
        break;
      case EQuestionAction.addToApiExposureCollectionFields:
        this.#dragStore.setApiExposureField({ index: this.index, isEditMode: false });
        break;
      case EQuestionAction.editToApiExposureCollectionFields:
        this.#dragStore.setApiExposureField({ index: this.index, isEditMode: true });
        break;
    }
    console.log("choose question", this.questionForm.value, "as filter field, index:", this.index);

    // this.#dragStore.
  


}

toggleRequierd(state: boolean) {
  this.questionForm.patchValue({ ...this.questionForm.value, requierd: state } as Question)
}
duplicateQuestion() {
  this.dupliacteQuestionEvent.emit()
}
}

